import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Chip,
  Tabs,
  Tab,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
} from '@mui/material';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import AddIcon from '@mui/icons-material/Add';
import { useTranslation } from 'react-i18next';

const SET_MANAGEMENT_DIALOG_COPY = {
  de: {
    title: 'Gespeicherte Sets verwalten',
    description: 'Sets sind vordefinierte Kombinationen von Parametern, Stationen oder Tags, die Sie schnell anwenden können.',
    tabs: {
      all: 'Alle Sets',
      parameters: 'Parameter',
      stations: 'Stationen',
      tags: 'Tags',
      mixed: 'Gemischt',
    },
    public: 'Öffentlich',
    load: 'Laden',
    parameterSummary: 'Parameter ({{count}}):',
    stationSummary: 'Stationen ({{count}}):',
    tagSummary: 'Tags ({{count}}):',
    more: '+ {{count}} weitere',
    createdBy: 'Erstellt von: {{value}}',
    empty: 'Keine Sets in dieser Kategorie vorhanden.',
    noteTitle: 'Hinweis:',
    noteBody: 'In der vollständigen Version können Sie hier eigene Sets erstellen, bearbeiten und löschen.',
    close: 'Schließen',
    create: 'Neues Set erstellen',
  },
  en: {
    title: 'Manage saved sets',
    description: 'Sets are predefined combinations of parameters, stations, or tags that you can apply quickly.',
    tabs: {
      all: 'All sets',
      parameters: 'Parameters',
      stations: 'Stations',
      tags: 'Tags',
      mixed: 'Mixed',
    },
    public: 'Public',
    load: 'Load',
    parameterSummary: 'Parameters ({{count}}):',
    stationSummary: 'Stations ({{count}}):',
    tagSummary: 'Tags ({{count}}):',
    more: '+ {{count}} more',
    createdBy: 'Created by: {{value}}',
    empty: 'No sets available in this category.',
    noteTitle: 'Note:',
    noteBody: 'In the full version, you will be able to create, edit, and delete your own sets here.',
    close: 'Close',
    create: 'Create new set',
  },
  pt: {
    title: 'Gerenciar conjuntos salvos',
    description: 'Conjuntos são combinações predefinidas de parâmetros, estações ou tags que você pode aplicar rapidamente.',
    tabs: {
      all: 'Todos os conjuntos',
      parameters: 'Parâmetros',
      stations: 'Estações',
      tags: 'Tags',
      mixed: 'Misto',
    },
    public: 'Público',
    load: 'Carregar',
    parameterSummary: 'Parâmetros ({{count}}):',
    stationSummary: 'Estações ({{count}}):',
    tagSummary: 'Tags ({{count}}):',
    more: '+ {{count}} adicionais',
    createdBy: 'Criado por: {{value}}',
    empty: 'Nenhum conjunto disponível nesta categoria.',
    noteTitle: 'Observação:',
    noteBody: 'Na versão completa, você poderá criar, editar e excluir seus próprios conjuntos aqui.',
    close: 'Fechar',
    create: 'Criar novo conjunto',
  },
};

/**
 * Set Management Dialog Component
 * Dialog for managing saved filter sets
 *
 * @param {boolean} open - Whether dialog is open
 * @param {Function} onClose - Handler for closing dialog
 * @param {Array} savedSets - All saved sets
 * @param {number} activeSetTab - Active tab index
 * @param {Function} setActiveSetTab - Active tab setter
 * @param {Function} handleLoadSet - Handler for loading set
 * @param {Function} getSetTypeBadge - Get set type badge label
 */
const SetManagementDialog = ({
  open,
  onClose,
  savedSets,
  activeSetTab,
  setActiveSetTab,
  handleLoadSet,
  getSetTypeBadge,
}) => {
  const { i18n } = useTranslation();
  const language = i18n.resolvedLanguage?.startsWith('pt')
    ? 'pt'
    : i18n.resolvedLanguage?.startsWith('en')
      ? 'en'
      : 'de';
  const copy = SET_MANAGEMENT_DIALOG_COPY[language];

  const filterSetsByTab = (set) => {
    if (activeSetTab === 0) return true; // All
    if (activeSetTab === 1) return set.type === 'parameters';
    if (activeSetTab === 2) return set.type === 'stations';
    if (activeSetTab === 3) return set.type === 'tags';
    if (activeSetTab === 4) return set.type === 'mixed';
    return true;
  };

  const filteredSets = savedSets ? savedSets.filter(filterSetsByTab) : [];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <BookmarkIcon />
          {copy.title}
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="textSecondary" paragraph>
          {copy.description}
        </Typography>

        {/* Tabs for different set types */}
        <Tabs
          value={activeSetTab}
          onChange={(e, v) => setActiveSetTab(v)}
          sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label={copy.tabs.all} />
          <Tab label={copy.tabs.parameters} />
          <Tab label={copy.tabs.stations} />
          <Tab label={copy.tabs.tags} />
          <Tab label={copy.tabs.mixed} />
        </Tabs>

        {/* Tab Content */}
        <Box sx={{ mt: 2 }}>
          {filteredSets.map(set => (
            <Paper
              key={set.id}
              variant="outlined"
              sx={{
                p: 2,
                mb: 2,
                cursor: 'pointer',
                '&:hover': { backgroundColor: 'action.hover' }
              }}
              onClick={() => {
                handleLoadSet(set);
                onClose();
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="h6" component="span">
                    {set.icon}
                  </Typography>
                  <Typography variant="h6" fontWeight="bold">
                    {set.name}
                  </Typography>
                  <Chip
                    label={getSetTypeBadge(set.type)}
                    size="small"
                    color={set.color}
                  />
                  {set.isPublic && (
                    <Chip
                      label={copy.public}
                      size="small"
                      variant="outlined"
                    />
                  )}
                </Box>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleLoadSet(set);
                    onClose();
                  }}
                >
                  {copy.load}
                </Button>
              </Box>

              <Typography variant="body2" color="textSecondary" paragraph>
                {set.description}
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {/* Show set contents */}
                {set.items.parameters && set.items.parameters.length > 0 && (
                  <Typography variant="caption" color="textSecondary">
                    <strong>{copy.parameterSummary.replace('{{count}}', set.items.parameters.length)}</strong> {set.items.parameters.join(', ')}
                  </Typography>
                )}
                {set.items.stations && set.items.stations.length > 0 && (
                  <Typography variant="caption" color="textSecondary">
                    <strong>{copy.stationSummary.replace('{{count}}', set.items.stations.length)}</strong> {set.items.stations.slice(0, 3).join(', ')}
                    {set.items.stations.length > 3 && ` ${copy.more.replace('{{count}}', set.items.stations.length - 3)}`}
                  </Typography>
                )}
                {set.items.tags && set.items.tags.length > 0 && (
                  <Typography variant="caption" color="textSecondary">
                    <strong>{copy.tagSummary.replace('{{count}}', set.items.tags.length)}</strong> {set.items.tags.join(', ')}
                  </Typography>
                )}
              </Box>

              <Divider sx={{ my: 1 }} />

              <Typography variant="caption" color="textSecondary">
                {copy.createdBy.replace('{{value}}', set.createdBy)}
              </Typography>
            </Paper>
          ))}

          {/* Show message if no sets match the filter */}
          {filteredSets.length === 0 && (
            <Alert severity="info">
              {copy.empty}
            </Alert>
          )}
        </Box>

        <Alert severity="info" sx={{ mt: 3 }}>
          <Typography variant="body2">
            <strong>{copy.noteTitle}</strong> {copy.noteBody}
          </Typography>
        </Alert>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{copy.close}</Button>
        <Button variant="contained" startIcon={<AddIcon />} disabled>
          {copy.create}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SetManagementDialog;
